import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Plus, Eye, EyeOff, Copy, Trash2, AlertCircle } from 'lucide-react';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const Vault = () => {
    const { t, i18n } = useTranslation();
    const { activeOrg } = useActiveOrg();
    const [secrets, setSecrets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [visibleSecrets, setVisibleSecrets] = useState({});
    const [newSecret, setNewSecret] = useState({ key: '', value: '', description: '' });
    const [error, setError] = useState(null);

    useEffect(() => {
        if (activeOrg) {
            fetchSecrets();
        } else {
            setSecrets([]);
            setLoading(false);
        }
    }, [activeOrg]);

    const fetchSecrets = async () => {
        try {
            setLoading(true);
            const res = await API.get('/secrets/');
            // Sadece aktif organizasyona ait olan secrets'ları filtrele
            const filtered = res.data.filter(secret => secret.organization === activeOrg?.id);
            setSecrets(filtered);
            setLoading(false);
        } catch (err) {
            console.error("Secrets fetch error:", err);
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setNewSecret({ key: '', value: '', description: '' });
        setError(null);
    };

    const handleCreate = async () => {
        if (!activeOrg) {
            setError(t('vault.no_active_org_error', 'Aktif bir organizasyon seçilmemiş.'));
            return;
        }
        if (!newSecret.key.trim() || !newSecret.value.trim()) {
            setError(t('vault.validation_error', 'Anahtar (Key) ve Değer (Value) alanları zorunludur.'));
            return;
        }
        try {
            const payload = {
                ...newSecret,
                organization: activeOrg.id
            };
            await API.post('/secrets/', payload);
            handleCloseModal();
            fetchSecrets();
        } catch (err) {
            console.error("Create error:", err);
            const errorMsg = err.response?.data?.detail || err.response?.data?.organization?.[0] || err.response?.data?.key?.[0] || t('vault.create_failed', 'Secret eklenirken hata oluştu.');
            setError(errorMsg);
        }
    };

    const handleDelete = async (id) => {
        const confirmMsg = i18n.language && i18n.language.startsWith('en')
            ? 'Are you sure you want to delete this secret?'
            : 'Bu şifreyi silmek istediğinize emin misiniz?';
        if (window.confirm(confirmMsg)) {
            try {
                await API.delete(`/secrets/${id}/`);
                fetchSecrets();
            } catch (err) {
                console.error("Delete error:", err);
            }
        }
    };

    const toggleVisibility = (id) => {
        setVisibleSecrets(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        const copiedMsg = i18n.language && i18n.language.startsWith('en')
            ? 'Copied to clipboard!'
            : 'Panoya kopyalandı!';
        alert(copiedMsg);
    };

    if (loading) return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('vault.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('vault.subtitle')}</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    {t('vault.new_secret')}
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {secrets.length === 0 ? (
                    <div className="p-8 text-center">
                        <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">{t('vault.no_secrets')}</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {secrets.map((secret) => (
                            <div key={secret.id} className="p-4 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="p-2 rounded-lg bg-purple-100">
                                            <Key className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-gray-900">{secret.key}</h3>
                                            <p className="text-sm text-gray-500">{secret.description || t('vault.no_description')}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="font-mono text-sm bg-gray-100 px-3 py-1 rounded">
                                            {visibleSecrets[secret.id] ? secret.decrypted_value || '••••••' : '••••••••'}
                                        </span>
                                        <button
                                            onClick={() => toggleVisibility(secret.id)}
                                            className="p-2 text-gray-400 hover:text-gray-600"
                                        >
                                            {visibleSecrets[secret.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => copyToClipboard(secret.decrypted_value || secret.key)}
                                            className="p-2 text-gray-400 hover:text-gray-600"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(secret.id)}
                                            className="p-2 text-red-400 hover:text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('vault.new_secret')}</h2>
                        
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2 border border-red-100 animate-shake">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span className="flex-1">{error}</span>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vault.key_label')}</label>
                                <input
                                    type="text"
                                    value={newSecret.key}
                                    onChange={(e) => setNewSecret({ ...newSecret, key: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder={t('vault.key_placeholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vault.value_label')}</label>
                                <input
                                    type="password"
                                    value={newSecret.value}
                                    onChange={(e) => setNewSecret({ ...newSecret, value: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder={t('vault.value_placeholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vault.desc_label')}</label>
                                <input
                                    type="text"
                                    value={newSecret.description}
                                    onChange={(e) => setNewSecret({ ...newSecret, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder={t('vault.desc_placeholder')}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleCreate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vault;
